import { useEffect, useRef } from "react";

// Calls refresh every intervalMs while the page is visible, and right away
// when the page becomes visible again with data older than intervalMs.
// Nothing is fetched while the tab is hidden.
const useAutoRefresh = (
    refresh: () => void,
    intervalMs: number,
    lastUpdated: number | null
) => {
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    const lastUpdatedRef = useRef(lastUpdated);
    lastUpdatedRef.current = lastUpdated;

    useEffect(() => {
        let timer: ReturnType<typeof setInterval> | undefined;

        const start = () => {
            if (timer === undefined) {
                timer = setInterval(() => refreshRef.current(), intervalMs);
            }
        };
        const stop = () => {
            clearInterval(timer);
            timer = undefined;
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                const age =
                    lastUpdatedRef.current === null
                        ? Infinity
                        : Date.now() - lastUpdatedRef.current;
                if (age >= intervalMs) refreshRef.current();
                start();
            } else {
                stop();
            }
        };

        if (document.visibilityState === "visible") start();
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            stop();
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange
            );
        };
    }, [intervalMs]);
};

export default useAutoRefresh;
